import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Search, ChevronDown } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTemplateCategories = () => {
  const { toast } = useToast();
  const [categories, setCategories]     = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchQuery, setSearchQuery]   = useState('');
  const [filterTypeId, setFilterTypeId] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen]         = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formName, setFormName]             = useState('');
  const [formTypeId, setFormTypeId]         = useState('');
  const [formOrder, setFormOrder]           = useState(0);
  const [saving, setSaving]                 = useState(false);

  const token = () => localStorage.getItem('admin_token');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/template-categories`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.status === 401) { window.location.href = '/admin/login'; return; }
      if (res.ok) setCategories(await res.json());
    } catch {
      toast({ title: 'Error', description: 'Failed to load categories', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    fetch(`${API_URL}/api/public/template-types`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setTemplateTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const openAdd = () => {
    setEditingCategory(null);
    setFormName('');
    setFormTypeId(filterTypeId || '');
    setFormOrder(categories.length);
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditingCategory(cat);
    setFormName(cat.name);
    setFormTypeId(cat.type_id);
    setFormOrder(cat.order ?? 0);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast({ title: 'Error', description: 'Theme name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const url = editingCategory
        ? `${API_URL}/api/admin/content/template-categories/${editingCategory.id}`
        : `${API_URL}/api/admin/content/template-categories`;
      const res = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ name: formName.trim(), type_id: formTypeId || null, order: formOrder }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to save');
      }
      toast({ title: 'Saved', description: `Theme ${editingCategory ? 'updated' : 'created'} successfully` });
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete theme "${cat.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/content/template-categories/${cat.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast({ title: 'Deleted', description: `Theme "${cat.name}" removed` });
      fetchAll();
    } catch {
      toast({ title: 'Error', description: 'Could not delete category', variant: 'destructive' });
    }
  };

  const filtered = categories.filter(c => {
    const matchSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchType   = !filterTypeId || c.type_id === filterTypeId;
    return matchSearch && matchType;
  });

  // Group by template type; ungrouped themes go in a "General" bucket
  const grouped = [
    ...templateTypes.map(tt => ({
      ...tt,
      items: filtered.filter(c => c.type_id === tt.id),
    })).filter(g => g.items.length > 0),
    {
      id: '__general__',
      name: 'General (No Type)',
      icon: '',
      items: filtered.filter(c => !c.type_id),
    },
  ].filter(g => g.items.length > 0);

  return (
    <AdminLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Template Themes</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Industry / niche themes that appear in the editor template browser.
            </p>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition-colors flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            Add Theme
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              placeholder="Search themes…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 w-56"
            />
          </div>
          <div className="relative">
            <select
              value={filterTypeId}
              onChange={e => setFilterTypeId(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
            >
              <option value="">All Types</option>
              {templateTypes.map(tt => (
                <option key={tt.id} value={tt.id}>{tt.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl flex flex-col items-center justify-center py-16">
            <p className="font-medium text-gray-700">No themes found</p>
            <p className="text-sm text-gray-400 mt-1">
              {categories.length === 0 ? 'Add your first theme above.' : 'Try a different search or filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(group => (
              <div key={group.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                {/* Group header */}
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2 bg-gray-50/60">
                  {group.icon && <span className="text-base">{group.icon}</span>}
                  <p className="font-semibold text-gray-800 text-sm">{group.name}</p>
                  <span className="ml-1 text-[11px] font-bold px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded-md">{group.items.length}</span>
                </div>

                {/* Category rows */}
                <div className="divide-y divide-gray-50">
                  {group.items.map(cat => (
                    <div key={cat.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50/60 transition-colors">
                      <p className="flex-1 text-sm font-medium text-gray-800">{cat.name}</p>
                      <span className="text-[11px] text-gray-400 tabular-nums">Order: {cat.order ?? 0}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(cat)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(cat)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <p className="font-semibold text-gray-900">{editingCategory ? 'Edit Theme' : 'Add Theme'}</p>
            </div>
            <div className="px-6 py-5 space-y-4">

              {/* Theme Name */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700">Theme Name *</label>
                <input
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="e.g. Medical, Football, Restaurant…"
                  className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                />
              </div>

              {/* Template Type (optional) */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700">
                  Associated Type <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <select
                    value={formTypeId}
                    onChange={e => setFormTypeId(e.target.value)}
                    className="w-full appearance-none px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                  >
                    <option value="">None (General)</option>
                    {templateTypes.map(tt => (
                      <option key={tt.id} value={tt.id}>{tt.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <p className="text-[11px] text-gray-400">Leave blank to make this theme available across all types.</p>
              </div>

              {/* Order */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700">Sort Order</label>
                <input
                  type="number"
                  value={formOrder}
                  onChange={e => setFormOrder(parseInt(e.target.value) || 0)}
                  min={0}
                  className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setDialogOpen(false)}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTemplateCategories;
